"use client";

// Google Tag Manager loader. Browser-side only.
//
// No-ops entirely when NEXT_PUBLIC_GTM_ID is unset — matching the graceful-no-op convention
// every other integration in .env.example follows (PostHog, Meta Pixel, Turnstile), so local
// dev and CI need no config.
//
// Unlike the Meta Pixel (components/analytics/MetaPixel.tsx), which doesn't load at all until
// cookie consent is accepted, this container loads on EVERY page load unconditionally — that
// was the marketer's explicit ask, and Google Ads needs the container present to run
// conversion *modelling* even for visitors who never accept. Compliance instead comes from
// Google Consent Mode v2: the inline script sets all four ad/analytics signals to `denied`
// before gtm.js is requested, and an effect below pushes `granted` only once the visitor
// accepts (lib/consent.ts), or back to `denied` on withdrawal.
//
// The consent-default block and the gtm.js loader MUST stay in one script tag, in this order —
// splitting them across renders (or loading gtm.js first) would let tags fire before Consent
// Mode is armed.
//
// strategy="beforeInteractive" (TRA-319 — the marketer couldn't find the tag in "View Source"
// or confirm it was in <head>, because it wasn't there): per Next's own Script docs
// (node_modules/next/dist/docs/01-app/03-api-reference/02-components/script.md), scripts with
// this strategy "will always be injected inside the head of the HTML document regardless of
// where it's placed in the component" AND are part of the server-rendered HTML from the start
// (unlike afterInteractive, which is injected into document.body by client JS after hydration
// and never appears in raw page source at all — whether it's actually working or not is
// invisible from "View Source" either way). This can't be used for the Meta Pixel — that one's
// render output depends on useConsent()'s hydrated value, which doesn't exist before
// hydration — but this component's
// script content is a static template with no such dependency, so the earlier strategy is
// available and is also a genuine improvement: it now runs before any Next.js JS at all,
// which is the earliest possible point to arm Consent Mode's default, not just "early".

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { type ConsentValue, useConsent } from "@/lib/consent";
import { GTM_ID, GTM_CONSENT_DENIED, GTM_CONSENT_GRANTED, callGtag, pushToDataLayer } from "@/lib/analytics/gtm";

const CONSENT_DEFAULT_JSON = JSON.stringify({ ...GTM_CONSENT_DENIED, wait_for_update: 500 });

export function GoogleTagManager() {
  const consent = useConsent();
  const pathname = usePathname();

  // The pathname GTM's own container-load / page-load trigger already covers. Seeded from
  // this effect's first run, NOT from the Script's onLoad — next/script never invokes onLoad
  // for this component, for two independent reasons that both apply here: it's an inline
  // <script> (`children`, not `src`), which never fires a DOM `load` event in the
  // afterInteractive code path (see next/dist/client/script.js's `children` branch: it runs
  // afterLoad() → onReady only, never onLoad — the same reasoning MetaPixel.tsx documents);
  // and separately, beforeInteractive scripts run before hydration, before
  // there's any mounted React tree to call an onLoad prop back into, so onLoad isn't wired up
  // for that strategy at all. Either reason alone would mean relying on onLoad here leaves
  // SPA pageviews never firing — hence the manual ref-based tracking below.
  const trackedPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!GTM_ID) return;
    if (trackedPathnameRef.current === null) {
      // First render — the container's own page-load trigger already covers this pathname.
      trackedPathnameRef.current = pathname;
      return;
    }
    if (pathname === trackedPathnameRef.current) return;
    trackedPathnameRef.current = pathname;
    pushToDataLayer({ event: "spa_page_view", page_path: pathname });
  }, [pathname]);

  // Previous consent value, so a redundant push is never sent (e.g. re-renders while the
  // visitor stays "accepted"). An undecided first load has both sides null and is skipped by
  // the guard below — the inline script already pushed the `denied` default, so nothing is
  // owed there. Any OTHER transition into null, though, is a withdrawal: CookieSettingsButton
  // (components/consent/CookieSettingsButton.tsx) calls clearConsent(), not
  // setConsent("rejected"), so "changed their mind after accepting" reads as accepted → null,
  // not accepted → rejected. Code review caught an earlier version of this effect
  // treating only "rejected" as a denial and silently leaving Ads storage granted forever
  // after a withdrawal.
  const trackedConsentRef = useRef<ConsentValue | null>(null);

  useEffect(() => {
    if (!GTM_ID) return;
    if (consent === trackedConsentRef.current) return;
    trackedConsentRef.current = consent;
    // Reaching this line at all means a real transition happened (the guard above already
    // filtered out "undecided the whole time"), so anything that isn't "accepted" — explicit
    // rejection or a withdrawal back to null — must deny.
    callGtag("consent", "update", consent === "accepted" ? GTM_CONSENT_GRANTED : GTM_CONSENT_DENIED);
  }, [consent]);

  if (!GTM_ID) return null;

  return (
    // The rule's App Router allowance (@next/eslint-plugin-next's
    // no-before-interactive-script-outside-document.js) checks whether the FILE PATH contains
    // an `app/` segment, so it doesn't recognize this component even though it's only ever
    // rendered from app/layout.tsx — beforeInteractive in the root layout is the officially
    // documented App Router pattern (node_modules/next/dist/docs/01-app/03-api-reference/
    // 02-components/script.md), this just lives in components/analytics/ like every other
    // analytics integration in this repo rather than inline in app/layout.tsx.
    // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
    <Script id="gtm" strategy="beforeInteractive">
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('consent', 'default', ${CONSENT_DEFAULT_JSON});
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${GTM_ID}');
      `}
    </Script>
  );
}
